import classNames from "classnames";
import moment from "moment";
import { ActionDiv } from "PFComponents/action_div";
import { CarouselBaseProps } from "PFComponents/carousel/carousel_base";
import { LoadingDots } from "PFComponents/loading_dots";
import { Typography } from "PFComponents/typography";
import { DATE_MONTH_YEAR } from "PFCore/helpers/date";
import { useMarketplaceNextAvailabilities } from "PFCore/hooks/queries/activities/marketplace/use_marketplace_next_availabilities";
import { NextAvailability as NextAvailabilityType } from "PFCore/services/marketplace/fetch_marketplace_next_availabilities";
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { useMarketplaceContext } from "../../../contexts/marketplace_context";
import { useStartDateFilter } from "../../../hooks/use_start_date_filter";
import { Tile } from "../../components";
import { Carousel } from "../../components/carousel";
import css from "./next_availability.module.scss";

type DateWithDurationProps = {
  startDate: string;
  daysCount: number;
  onClick?: () => void;
};

const DateWithDuration = ({ startDate, daysCount, onClick }: DateWithDurationProps): JSX.Element => {
  const { t } = useTranslation("marketplace", { keyPrefix: "dashboard.nextAvailability" });
  const momentDate = moment(startDate);
  const day = momentDate.date();
  const monthYear = momentDate.format(DATE_MONTH_YEAR);
  const weeks = Math.floor(daysCount / 7);
  const days = daysCount % 7;
  const formattedWeeks = weeks > 0 ? `${weeks} ${t("week", { count: weeks })}` : "";
  const formattedDays = days > 0 ? `${days} ${t("day", { count: days })}` : "";
  const isBothWeeksAndDays = formattedWeeks && formattedDays;

  return (
    <ActionDiv onClick={onClick} className={css.item}>
      <Typography variant="h1" className={css.day} noMargin>
        {day}
      </Typography>
      <Typography variant="h5" className={classNames({ [css.smallFont]: isBothWeeksAndDays })} noMargin>
        {`${monthYear} - ${formattedWeeks} ${formattedDays}`}
      </Typography>
    </ActionDiv>
  );
};

export const NextAvailability = (): JSX.Element => {
  const { t } = useTranslation("marketplace", { keyPrefix: "dashboard.nextAvailability" });
  const { data, isLoading } = useMarketplaceNextAvailabilities();
  const history = useHistory();
  const { serialize } = useStartDateFilter();
  const { clearAllFilters } = useMarketplaceContext();

  const handleItemClick = useCallback(
    ({ startDate, endDate }: Pick<NextAvailabilityType, "startDate" | "endDate">) => {
      clearAllFilters();
      history.push(
        `/marketplace/work_opportunities/matching_roles?${serialize({
          from: startDate,
          to: endDate
        })}`
      );
    },
    [history, serialize]
  );

  const items: CarouselBaseProps["items"] = useMemo(
    () =>
      (data?.entries as NextAvailabilityType[])?.map(({ daysCount, ...item }, index) => ({
        id: index,
        content: (
          <DateWithDuration
            onClick={() => handleItemClick(item)}
            startDate={item.startDate}
            daysCount={daysCount}
          />
        )
      })) || [],
    [data?.entries, handleItemClick]
  );

  return (
    <Tile title={t("title")}>
      <div className={css.tileContentWrapper}>
        {isLoading && <LoadingDots circlesEnabled circleSize="xs" />}
        {items.length > 0 && <Carousel items={items} />}
      </div>
    </Tile>
  );
};
